import React from 'react';
import cx from 'classnames';
import Loader from 'components/common/ui/Loader';
import { DownloadIcon } from '@zola/zola-ui/src/components/SvgIcons/Download';
import { DeleteIcon } from '@zola/zola-ui/src/components/SvgIcons/DeleteIcon';
import iconUpload from 'assets/images/guestlist/iconUpload.png';
import { ButtonV2 } from '@zola/zola-ui/src/components/Button';
import { useForceUpdateOnWindowResize } from '@zola/zola-ui/src/hooks/useForceUpdateOnWindowResize';
import { notDesktop, isMobile } from '@zola-helpers/client';

import styles from 'components/common/ImageUploadV2/UploadInterface.module.less';
import { StyledButton, EmptyContainer, Tip } from './UploadInterface.styles';

type UploadButtonProps = {
  busy: boolean;
  onClick: () => void;
  imageUrl?: string | null;
  onClearImage: () => void;
  previewShape?: 'default' | 'circle';
  isEditWebsiteV2: boolean;
};

/**
 * A reusable interface for triggering Uploadcare modal. This component is designed to
 * be a simple controlled UI component so it can be reused in different situations.
 *
 * @param busy - a busy state for rendering loader
 * @param onClick - click event handler. Mostly using for re-upload another photo
 * @param clearImage - click event handler. Mostly using it for clear image.
 * @param imageUrl - URL for showing the preview.
 * @constructor
 */
const UploadInterface: React.FC<UploadButtonProps> = ({
  busy = false,
  onClick,
  imageUrl,
  previewShape = 'default',
  onClearImage,
  isEditWebsiteV2,
}) => {
  useForceUpdateOnWindowResize();
  if (busy) return <Loader />;

  if (imageUrl) {
    return (
      <div className={styles.uploadedContainer}>
        {imageUrl ? (
          <img
            className={cx(styles.img, {
              [styles.circle]: previewShape === 'circle',
              [styles.roundedBorder]: isEditWebsiteV2,
            })}
            src={imageUrl}
            alt="User uploaded file"
          />
        ) : null}
        <div className={styles.buttons}>
          <button type="button" className={styles.imgActionCta} onClick={onClick}>
            <DownloadIcon className={styles.icon} flip="vertical" />
            Change photo
          </button>
          <button type="button" className={styles.imgActionCta} onClick={onClearImage}>
            <DeleteIcon className={styles.icon} />
            Remove Photo
          </button>
        </div>
      </div>
    );
  }
  if (isEditWebsiteV2) {
    return (
      <EmptyContainer>
        <Tip>
          <span role="img" aria-label="Light bulb">
            💡
          </span>
          <strong>Pro Tip:</strong> A horizontal photo will look best.
        </Tip>
        <StyledButton
          className={styles.browseBtn}
          onClick={onClick}
          fullWidth={notDesktop()}
          variant={isMobile() ? 'primary' : 'secondary'}
        >
          Browse Photos
        </StyledButton>
      </EmptyContainer>
    );
  }

  return (
    <div className={styles.container}>
      <img className={styles.iconUpload} src={iconUpload} alt="Click here to upload file" />
      <p className={styles.tip}>
        <span role="img" aria-label="Light bulb">
          💡
        </span>
        <strong>Pro Tip:</strong> A horizontal photo will look best.
      </p>
      <ButtonV2
        className={styles.browseBtn}
        onClick={onClick}
        fullWidth={notDesktop()}
        variant={isMobile() ? 'primary' : 'secondary'}
      >
        Browse Photos
      </ButtonV2>
    </div>
  );
};

export default UploadInterface;
